import {
  NOYAU_TEXT_FLIP,
  PALIER_RAMP_MAX,
  SABOTAGE_PALIER,
  palierRampStep,
} from '@mss_creator/shared';
import type { Noyau, NoyauColorToken } from '@mss_creator/shared';

/**
 * The `noyau-gradient` matrix header theme: each palier header ramps from pure
 * white (palier 0) to the company's real noyau colour at full strength
 * (palier 6), in even steps. Palier -10 (sabotage) sits *outside* the ramp
 * rather than below it, so it gets a neutral grey instead of a seventh colour.
 *
 * The ramp itself — its length, its text-contrast flip table, what counts as
 * off-ramp — lives in `@mss_creator/shared`'s `palierRampStep`, because the
 * export renders the same theme and must agree step for step. This module owns
 * only the *grid's* colour vocabulary: custom properties and `color-mix`, both
 * of which need a stylesheet the export never has (issue #8).
 */

/**
 * Base CSS colour per noyau token.
 *
 * `bleu-nuit` deliberately reads `--color-bleu-nuit-true`, not
 * `--color-bleu-nuit`: `NoyauAccentScope` shadows the latter for its whole
 * subtree to implement the `matrix-noyaux-accent` preview. Reading the shadowed
 * variable would let that preview silently recolour the gradient **only for SGP
 * companies** (the one noyau that maps to bleu-nuit) — inconsistent behaviour
 * depending on which company you happen to be viewing.
 */
const NOYAU_BASE_VAR: Record<NoyauColorToken, string> = {
  'bleu-nuit': 'var(--color-bleu-nuit-true)',
  'bleu-ciel': 'var(--color-bleu-ciel)',
  vert: 'var(--color-vert)',
  jaune: 'var(--color-jaune)',
  rouge: 'var(--color-rouge)',
  rose: 'var(--color-rose)',
};

/** Re-exported so the contrast test keeps reading the flip table from the module that applies it. */
export { NOYAU_TEXT_FLIP };

/** Palier -10 — sabotage. Neutral, off-ramp. noir on it is 14.50:1. */
const SABOTAGE_BACKGROUND = 'var(--color-gray-200)';

export interface PalierGradient {
  /** `background` for the `<th>` — a `color-mix()` expression, or a flat token off-ramp. */
  background: string;
  /** Text colour meeting WCAG AA against `background`. */
  color: string;
  /**
   * Selection ring colour. Deliberately the same as `color` rather than a
   * themed colour: a ring tinted from the ramp is invisible at both ends — at
   * palier 0 the step *is* white, and at bleu-nuit palier 6 the ring would
   * equal the background. Reusing the text colour guarantees >=4.5:1 against
   * the header's own background at every step, which is stricter than the 3:1
   * WCAG asks of a UI component.
   */
  ring: string;
}

/**
 * Background / text / ring colours for one palier header under the
 * `noyau-gradient` theme.
 *
 * The `in srgb` colour space is load-bearing: `NOYAU_TEXT_FLIP` was computed
 * with sRGB mixing, and `in oklab` shifts every mid-tone enough to invalidate
 * it. The export mixes the same ratio itself, in sRGB, for the same reason.
 */
export function palierGradient(noyau: Noyau, palierNumber: number): PalierGradient {
  const step = palierRampStep(noyau, palierNumber);

  if (step.kind !== 'ramp') {
    return {
      background: step.kind === 'sabotage' ? SABOTAGE_BACKGROUND : 'var(--color-blanc)',
      color: 'var(--color-noir)',
      ring: 'var(--color-noir)',
    };
  }

  const textColor = step.textIsBlanc ? 'var(--color-blanc)' : 'var(--color-noir)';
  return {
    background: `color-mix(in srgb, ${NOYAU_BASE_VAR[step.token]} ${step.percent}%, white)`,
    color: textColor,
    ring: textColor,
  };
}

/** Exposed for the contrast test, which mixes these itself to verify `NOYAU_TEXT_FLIP`. */
export const GRADIENT_INTERNALS = {
  RAMP_MAX: PALIER_RAMP_MAX,
  NOYAU_BASE_VAR,
  SABOTAGE_PALIER,
} as const;
